/**
 * GapDate MDXEditor plugin — inline static-date chip.
 *
 * MARKDOWN SYNTAX:
 *   :GapDate{date="YYYY-MM-DD"}
 *
 * The chip displays "Today" / "Tomorrow" / "Mon, Jan 5"; clicking it re-opens the
 * calendar submenu for editing.
 */

import { DirectiveDescriptor, DirectiveEditorProps, useMdastNodeUpdater, useCellValue, readOnly$ } from '@mdxeditor/editor'
import Box from '@mui/material/Box'
import EventIcon from '@mui/icons-material/Event'
import { observer } from 'mobx-react-lite'
import { themeConfig } from '@gap/shared/theme'
import { useRef } from 'react'
import DateSubmenuPopover from '../../popover/DateSubmenuPopover'
import GapDateStore from './GapDateStore'
import useStore from 'reusable/mobx-store-utils'

const DIRECTIVE_NAME = 'GapDate'

const GapDateComponent = observer(function GapDateComponent({ mdastNode }: DirectiveEditorProps) {
   const isReadOnly = useCellValue(readOnly$)
   const updateMdastNode = useMdastNodeUpdater()
   const chipRef = useRef<HTMLSpanElement>(null)

   const gapDateStore = useStore(sp => new GapDateStore(sp), {
      updateMdastNode,
      attributes: mdastNode.attributes as Record<string, string> | undefined,
      chipRef
   })

   if (isReadOnly) {
      return `${gapDateStore.label} `
   }

   return (
      <>
         <Box
            component="span"
            ref={chipRef}
            onMouseDown={gapDateStore.handleChipMouseDown}
            sx={{
               display: 'inline-flex',
               alignItems: 'center',
               gap: '4px',
               padding: '2px 8px 2px 6px',
               borderRadius: '9999px',
               background: themeConfig.system.info[50],
               color: themeConfig.system.info[700],
               fontSize: 14,
               fontWeight: 500,
               lineHeight: 1.2,
               cursor: 'pointer',
               verticalAlign: 'middle',
               '&:hover': { background: themeConfig.system.info[100] },
            }}
         >
            <EventIcon sx={{ fontSize: 14, color: themeConfig.system.info[600] }} />
            {gapDateStore.label}
         </Box>
         {gapDateStore.popover && (
            <DateSubmenuPopover
               anchor={gapDateStore.popover}
               onSelect={gapDateStore.handlePicked}
               onClose={gapDateStore.handleClose}
            />
         )}
      </>
   )
})

export const GapDateDirectiveDescriptor: DirectiveDescriptor = {
   name: DIRECTIVE_NAME,
   type: 'textDirective',
   testNode(node) {
      return node.name === DIRECTIVE_NAME
   },
   attributes: ['date'],
   hasChildren: false,
   Editor: GapDateComponent,
}
